import React, { useState } from 'react';
import {
	Card,
	CardBody,
	Text,
	Image,
	CardFooter,
	Button,
	Badge,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalFooter,
	ModalBody,
	ModalCloseButton,
	Box,
	HStack,
	Flex,
	IconButton,
	Divider,
	Stack,
	Wrap,
	WrapItem,
	Spinner,
} from '@chakra-ui/react';
import { AddIcon, MinusIcon, CheckIcon, ViewIcon } from '@chakra-ui/icons';
import { connect } from 'react-redux';
import { AddItemToCart } from '../redux/actions/cartActions';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import logoImage from '../assets/logo.png';

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

const ProductCard = ({
	image,
	name,
	description,
	price,
	product_id,
	AddItemToCart,
	unit,
	quantityPerUnit,
	catalogMode,
	out_of_stock,
	has_variants,
}: any) => {
	const navigate = useNavigate();
	const [quantity, setQuantity] = useState(1);
	const [addedToCart, setAddedToCart] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [imgLoaded, setImgLoaded] = useState(false);
	const [variants, setVariants] = useState<any[]>([]);
	const [variantsLoading, setVariantsLoading] = useState(false);
	const [selectedPerType, setSelectedPerType] = useState<Record<string, number>>({});
	const [variantsFetched, setVariantsFetched] = useState(false);

	const knownHasVariants = !!(has_variants == 1 || has_variants === true || (variantsFetched && variants.length > 0));

	// Only consider variants that are priced and in stock for selection
	const availableVariants = variants.filter((v) => v.final_price && parseFloat(v.final_price) > 0);
	const hasVariantPrices = availableVariants.length > 0;
	const listingPrice = Number(price) || 0;
	const isNotSold = knownHasVariants
		? (variantsFetched ? !hasVariantPrices : listingPrice <= 0)
		: listingPrice <= 0;
	const effectivelyNotSold = isNotSold || !!out_of_stock;

	// Derive unique attribute types only from available (priced) variants
	const attrTypes: string[] = availableVariants.length > 0
		? [...new Set(availableVariants.flatMap((v) => v.attributes.map((a: any) => a.attribute_name)))]
		: [];

	const allSelected = attrTypes.length > 0 && attrTypes.every((t) => selectedPerType[t] != null);

	const matchedVariant = allSelected
		? availableVariants.find((v) =>
			attrTypes.every((t) =>
				v.attributes.find((a: any) => a.attribute_name === t && a.attribute_id === selectedPerType[t])
			)
		)
		: null;

	const displayPrice = matchedVariant
		? Number(matchedVariant.final_price)
		: listingPrice;

	const selectedVariantOutOfStock = matchedVariant ? !!matchedVariant.out_of_stock : false;
	const isOutOfStock = !!out_of_stock || selectedVariantOutOfStock;

	const hasVariants = knownHasVariants;
	const needsSelection = hasVariants && (!variantsFetched || !allSelected);

	const openModal = async () => {
		setIsModalOpen(true);
		if (!variantsFetched) {
			setVariantsLoading(true);
			try {
				const res = await axios.get(
					catalogMode ? `${BASE_URL}/api/catalog/product-variants/${product_id}` : `${BASE_URL}/api/product-variants/${product_id}`,
					catalogMode ? {} : { headers: { 'x-auth-token': localStorage.getItem('x-auth-token') || '' } }
				);
				setVariants(Array.isArray(res.data) ? res.data : []);
			} catch {
				setVariants([]);
			} finally {
				setVariantsFetched(true);
				setVariantsLoading(false);
			}
		}
	};

	const closeModal = () => {
		setIsModalOpen(false);
		setQuantity(1);
		setAddedToCart(false);
		setSelectedPerType({});
	};

	const handleAddToCart = (e?: React.MouseEvent) => {
		e?.stopPropagation();
		if (effectivelyNotSold || isOutOfStock) return;
		if (hasVariants && !allSelected) {
			openModal();
			return;
		}
		AddItemToCart({
			image, name, description,
			price: displayPrice, product_id, quantity,
			variant_id: matchedVariant?.variant_id || null,
		});
		setAddedToCart(true);
		setTimeout(() => setAddedToCart(false), 2500);
	};

	const increase = (e: React.MouseEvent) => {
		e.stopPropagation();
		setQuantity((q) => q + 1);
	};
	const decrease = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (quantity > 1) setQuantity((q) => q - 1);
	};

	const imgSrc = image ? `${BASE_URL}/api/image?filename=${image}` : logoImage;

	const renderCardPrice = () => {
		if (catalogMode) {
			return <Text fontSize="sm" fontWeight="semibold" color="gray.500">Contact for price</Text>;
		}
		if (isNotSold) {
			return <Text fontSize="sm" fontWeight="semibold" color="gray.400">Not sold at your facility</Text>;
		}
		if (hasVariants) {
			return (
				<HStack spacing={2} align="baseline">
					<Text fontSize="lg" fontWeight="bold" color="blue.600">From ${listingPrice.toFixed(2)}</Text>
					<Badge colorScheme="purple" fontSize="0.65rem">Options</Badge>
				</HStack>
			);
		}
		return <Text fontSize="lg" fontWeight="bold" color="blue.600">${listingPrice.toFixed(2)}</Text>;
	};

	return (
		<>
			<Card
				position="relative"
				borderRadius="2xl"
				overflow="hidden"
				boxShadow="sm"
				border="1px solid"
				borderColor="gray.100"
				bg="white"
				transition="all 0.2s"
				_hover={{ boxShadow: 'lg', transform: 'translateY(-3px)', borderColor: 'gray.200' }}
				cursor="pointer"
				onClick={openModal}
			>
				{isNotSold && !catalogMode && (
					<Badge position="absolute" top={3} left={3} colorScheme="gray" borderRadius="md" px={2} py={0.5} fontSize="xs" zIndex={1}>
						Not Sold
					</Badge>
				)}
				{hasVariants && !isNotSold && (
					<Badge position="absolute" top={3} left={3} colorScheme="purple" borderRadius="md" px={2} py={0.5} fontSize="xs" zIndex={1}>
						Options
					</Badge>
				)}

				{/* Quick View hover overlay */}
				<Box position="absolute" top={3} right={3} zIndex={1} opacity={0} _groupHover={{ opacity: 1 }} transition="opacity 0.2s">
					<IconButton
						icon={<ViewIcon />}
						aria-label="Quick view"
						size="sm"
						borderRadius="full"
						bg="white"
						boxShadow="md"
						onClick={(e) => { e.stopPropagation(); openModal(); }}
					/>
				</Box>

				{/* Image */}
				<Box h="200px" bg="gray.50" display="flex" alignItems="center" justifyContent="center" overflow="hidden" position="relative">
					{!imgLoaded && (
						<Box position="absolute" inset={0} display="flex" alignItems="center" justifyContent="center" bg="gray.50">
							<Image src={logoImage} alt="Loading" maxH="80px" opacity={0.4} />
						</Box>
					)}
					<Image
						src={imgSrc}
						alt={name}
						maxH="170px"
						maxW="85%"
						objectFit="contain"
						onLoad={() => setImgLoaded(true)}
						style={{ opacity: imgLoaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
					/>
				</Box>

				<CardBody pb={2} pt={3}>
					<Text fontWeight="semibold" fontSize="sm" color="gray.800" noOfLines={2} lineHeight="snug" mb={1}>
						{name}
					</Text>
					{unit && quantityPerUnit && (
						<Text fontSize="xs" color="gray.500" mb={1}>{quantityPerUnit} per {unit}</Text>
					)}
					{renderCardPrice()}
				</CardBody>

				<CardFooter pt={0} pb={3} px={4}>
					{catalogMode ? (
						<Button w="full" size="sm" colorScheme="green" borderRadius="lg" onClick={(e) => { e.stopPropagation(); navigate('/contact'); }} fontWeight="semibold">
							Contact to Order
						</Button>
					) : isOutOfStock ? (
						<Button w="full" size="sm" colorScheme="red" borderRadius="lg" isDisabled onClick={(e) => e.stopPropagation()} fontWeight="semibold">
							Out of Stock
						</Button>
					) : isNotSold ? (
						<Button w="full" size="sm" colorScheme="gray" borderRadius="lg" isDisabled onClick={(e) => e.stopPropagation()} fontWeight="semibold">
							Not Available
						</Button>
					) : addedToCart ? (
						<Button w="full" size="sm" colorScheme="green" leftIcon={<CheckIcon />} onClick={(e) => e.stopPropagation()} borderRadius="lg">
							Added to Cart!
						</Button>
					) : hasVariants ? (
						<Button w="full" size="sm" colorScheme="blue" borderRadius="lg" onClick={(e) => { e.stopPropagation(); openModal(); }} fontWeight="semibold">
							Select Options
						</Button>
					) : (
						<HStack w="full" spacing={2} onClick={(e) => e.stopPropagation()}>
							<HStack border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
								<IconButton icon={<MinusIcon boxSize={2} />} aria-label="Decrease" size="sm" variant="ghost" onClick={decrease} isDisabled={quantity <= 1} minW={7} h={8} />
								<Text fontSize="sm" fontWeight="medium" minW={5} textAlign="center">{quantity}</Text>
								<IconButton icon={<AddIcon boxSize={2} />} aria-label="Increase" size="sm" variant="ghost" onClick={increase} minW={7} h={8} />
							</HStack>
							<Button flex={1} size="sm" colorScheme={isOutOfStock ? "red" : "blue"} borderRadius="lg" onClick={handleAddToCart} fontWeight="semibold" isDisabled={isOutOfStock}>
								Add to Cart
							</Button>
						</HStack>
					)}
				</CardFooter>
			</Card>

			{/* Quick View Modal */}
			<Modal isOpen={isModalOpen} onClose={closeModal} size="lg" isCentered>
				<ModalOverlay bg="blackAlpha.600" />
				<ModalContent borderRadius="2xl" overflow="hidden">
					<ModalCloseButton zIndex={10} />
					<Box bg="gray.50" h="280px" display="flex" alignItems="center" justifyContent="center" position="relative">
						<Image src={imgSrc} alt={name} maxH="240px" maxW="70%" objectFit="contain" />
					</Box>
					<ModalHeader pb={1} pt={4}>{name}</ModalHeader>
					<ModalBody pt={0}>
						<Stack spacing={3}>
							{description && <Text fontSize="sm" color="gray.600">{description}</Text>}
							{unit && quantityPerUnit && (
								<HStack spacing={4} fontSize="sm" color="gray.500">
									<Text>Sold in <strong>{unit}s</strong></Text>
									<Text><strong>{quantityPerUnit}</strong> per {unit}</Text>
								</HStack>
							)}
							<Divider />

							{/* Variant Selectors */}
							{variantsLoading && <Spinner size="sm" color="blue.400" />}
							{!variantsLoading && attrTypes.map((typeName) => {
								const options = availableVariants.reduce((acc: any[], v) => {
									const a = v.attributes.find((a: any) => a.attribute_name === typeName);
									if (a && !acc.find((x) => x.attribute_id === a.attribute_id)) acc.push(a);
									return acc;
								}, []);
								return (
									<Box key={typeName}>
										<Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
											{typeName}
										</Text>
										<Wrap spacing={2}>
											{options.map((opt) => (
												<WrapItem key={opt.attribute_id}>
													<Button
														size="sm"
														borderRadius="lg"
														variant={selectedPerType[typeName] === opt.attribute_id ? 'solid' : 'outline'}
														colorScheme={selectedPerType[typeName] === opt.attribute_id ? 'blue' : 'gray'}
														onClick={() => setSelectedPerType((prev) => ({ ...prev, [typeName]: opt.attribute_id }))}
													>
														{opt.attribute_value}
													</Button>
												</WrapItem>
											))}
										</Wrap>
									</Box>
								);
							})}
							{!variantsLoading && hasVariants && variantsFetched && !hasVariantPrices && !catalogMode && (
								<Text fontSize="sm" color="gray.500">No priced options available at your facility.</Text>
							)}
							{!variantsLoading && hasVariants && hasVariantPrices && !allSelected && (
								<Text fontSize="xs" color="orange.500">Please select all options before adding to cart.</Text>
							)}

							<Flex justify="space-between" align="center">
								<Box>
									<Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide">
										{catalogMode
											? 'Pricing'
											: hasVariants && !matchedVariant
												? 'Starting at'
												: 'Price per unit'}
									</Text>
									{catalogMode ? (
										<Text fontSize="lg" fontWeight="semibold" color="gray.500">Contact for price</Text>
									) : effectivelyNotSold && !catalogMode ? (
										<Text fontSize="lg" fontWeight="semibold" color="gray.400">Not sold at your facility</Text>
									) : isOutOfStock ? (
										<Text fontSize="lg" fontWeight="semibold" color="red.500">Out of Stock</Text>
									) : (
										<Text fontSize="2xl" fontWeight="bold" color="blue.600">${displayPrice.toFixed(2)}</Text>
									)}
								</Box>
								<Box textAlign="right">
									{effectivelyNotSold && !isOutOfStock && !catalogMode && <Badge colorScheme="gray">Not Sold</Badge>}
									{isOutOfStock && <Badge colorScheme="red">Out of Stock</Badge>}
									{!effectivelyNotSold && !isOutOfStock && <Badge colorScheme="green">In Stock</Badge>}
								</Box>
							</Flex>

							{!effectivelyNotSold && !catalogMode && (
								<>
									<Flex justify="space-between" align="center">
										<Text fontSize="sm" fontWeight="medium" color="gray.600">Quantity</Text>
										<HStack spacing={0} border="1px solid" borderColor="gray.200" borderRadius="lg" overflow="hidden">
											<IconButton icon={<MinusIcon boxSize={2.5} />} aria-label="Decrease" size="sm" variant="ghost" onClick={() => setQuantity((q) => Math.max(1, q - 1))} isDisabled={quantity <= 1} h={9} w={9} />
											<Text px={4} fontSize="md" fontWeight="semibold">{quantity}</Text>
											<IconButton icon={<AddIcon boxSize={2.5} />} aria-label="Increase" size="sm" variant="ghost" onClick={() => setQuantity((q) => q + 1)} h={9} w={9} />
										</HStack>
									</Flex>
									<Flex justify="space-between" align="center" bg="blue.50" px={4} py={2} borderRadius="lg">
										<Text fontSize="sm" color="blue.600">Total</Text>
										<Text fontSize="lg" fontWeight="bold" color="blue.700">${(displayPrice * quantity).toFixed(2)}</Text>
									</Flex>
								</>
							)}
						</Stack>
					</ModalBody>
					<ModalFooter pt={2} gap={2}>
						{catalogMode ? (
							<Button w="full" colorScheme="green" borderRadius="xl" size="lg" fontWeight="bold" onClick={() => navigate('/contact')}>
								Contact to Order
							</Button>
						) : isOutOfStock ? (
							<Button w="full" colorScheme="red" borderRadius="xl" size="lg" isDisabled fontWeight="bold">
								Out of Stock
							</Button>
						) : effectivelyNotSold ? (
							<Button w="full" colorScheme="gray" borderRadius="xl" size="lg" isDisabled fontWeight="bold">
								Not Available at Your Facility
							</Button>
						) : addedToCart ? (
							<Button w="full" colorScheme="green" leftIcon={<CheckIcon />} borderRadius="xl" size="lg">
								Added to Cart!
							</Button>
						) : (
							<Button
								w="full"
								colorScheme="blue"
								borderRadius="xl"
								size="lg"
								isDisabled={needsSelection}
								onClick={handleAddToCart}
								fontWeight="bold"
							>
								{needsSelection
									? 'Select Options'
									: `Add ${quantity > 1 ? `${quantity} ` : ''}to Cart — $${(displayPrice * quantity).toFixed(2)}`}
							</Button>
						)}
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};

const mapDispatchToProps = { AddItemToCart };
export default connect(null, mapDispatchToProps)(ProductCard);
